import * as React from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import CalendarTodayRoundedIcon from '@mui/icons-material/CalendarTodayRounded';
import TodayIcon from '@mui/icons-material/Today';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';

function ButtonField(props) {
  const {
    setOpen,
    label,
    id,
    disabled,
    InputProps: { ref } = {},
    inputProps: { 'aria-label': ariaLabel } = {},
  } = props;

  return (
    <Button
      variant="outlined"
      id={id}
      disabled={disabled}
      ref={ref}
      aria-label={ariaLabel}
      size="small"
      onClick={() => setOpen?.((prev) => !prev)}
      startIcon={<CalendarTodayRoundedIcon fontSize="small" />}
      sx={{ minWidth: 'fit-content' }}
    >
      {label ? `${label}` : 'Pick a date'}
    </Button>
  );
}

ButtonField.propTypes = {
  disabled: PropTypes.bool,
  id: PropTypes.string,
  inputProps: PropTypes.shape({
    'aria-label': PropTypes.string,
  }),
  InputProps: PropTypes.shape({
    endAdornment: PropTypes.node,
    startAdornment: PropTypes.node,
  }),
  label: PropTypes.node,
  setOpen: PropTypes.func,
};

export default function CustomDatePicker() {
  // Default to today's date
  const [value, setValue] = React.useState(dayjs());
  const [open, setOpen] = React.useState(false);

  const handleSetToday = () => {
    setValue(dayjs());
  };

  const isToday = value && value.isSame(dayjs(), 'day');

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <DatePicker
          value={value}
          label={value == null ? null : value.format('MMM DD, YYYY')}
          onChange={(newValue) => setValue(newValue)}
          slots={{ field: ButtonField }}
          slotProps={{
            field: { setOpen },
            nextIconButton: { size: 'small' },
            previousIconButton: { size: 'small' },
            actionBar: {
              actions: ['today', 'clear', 'accept'],
            },
          }}
          open={open}
          onClose={() => setOpen(false)}
          onOpen={() => setOpen(true)}
          views={['day', 'month', 'year']}
        />
        <Tooltip title="Jump to today">
          <IconButton
            size="small"
            onClick={handleSetToday}
            sx={{
              bgcolor: isToday ? 'primary.main' : 'action.hover',
              color: isToday ? 'white' : 'text.secondary',
              '&:hover': {
                bgcolor: isToday ? 'primary.dark' : 'action.selected',
              },
            }}
          >
            <TodayIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </LocalizationProvider>
  );
}
